import { forwardRef, useState } from 'react'
import { json } from '@codemirror/lang-json'
import { EditorView } from '@codemirror/view'
import { useFormContext } from '@iveco-ti/iveco-ds/hook-form'
import CodeMirror from '@uiw/react-codemirror'
import { tv, VariantProps } from 'tailwind-variants'

const jsonInputStyles = tv({
  slots: {
    container: 'space-y-2',
    label: 'block text-sm font-medium text-base-700',
    editor: '',
    helper: 'text-sm',
  },
  variants: {
    state: {
      default: {
        helper: 'text-base-600',
      },
      error: {
        helper: 'text-danger-500',
      },
      success: {
        helper: 'text-success-500',
      },
    },
  },
  defaultVariants: {
    state: 'default',
  },
})

export interface JsonInputProps extends VariantProps<typeof jsonInputStyles> {
  name?: string
  label?: string
  placeholder?: string
  helperText?: string
  value?: string
  onChange?: (value: string) => void
  disabled?: boolean
  className?: string
}

export const JsonInput = forwardRef<HTMLDivElement, JsonInputProps>(
  (
    {
      name,
      label,
      placeholder = '{}',
      helperText,
      value,
      onChange,
      disabled = false,
      state = 'default',
      className,
      ...props
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState(value || '')

    const formContext = useFormContext()
    const formValue =
      formContext && name ? formContext.watch?.(name) : undefined
    const formError =
      formContext && name ? formContext.formState?.errors?.[name] : undefined

    const currentValue =
      formValue !== undefined
        ? formValue
        : value !== undefined
          ? value
          : internalValue
    const finalState = formError ? 'error' : state
    const finalHelperText = (formError?.message as string) || helperText

    const styles = jsonInputStyles({ state: finalState })

    const extensions = [
      json(),
      EditorView.theme({
        '&': {
          fontSize: '0.875rem',
          outline: 'none !important',
        },
        '.cm-content': {
          padding: '0.375rem 0',
          minHeight: '7.5rem',

          borderRadius: 'calc(var(--radius) - 2px)',
        },
        '.cm-line': {
          background: 'transparent',
        },
        '.cm-scroller': {
          fontFamily: 'inherit',
          maxHeight: '15.625rem',
          overflowY: 'auto',
          borderRadius: 'calc(var(--radius) - 2px)',
        },
        '.cm-focused': {
          outline: 'none !important',
        },
        '.cm-editor': {
          border: 'none',
          backgroundColor: 'transparent',
          outline: 'none !important',
          borderRadius: 'calc(var(--radius) - 2px)',
        },
        '.cm-editor.cm-disabled': {
          color: 'var(--color-base-400)',
          backgroundColor: 'var(--color-gray-50)',
          cursor: 'not-allowed',
          outline: 'none !important',
          opacity: '0.6',
        },
        '.cm-disabled .cm-content': {
          color: 'var(--color-base-400)',
          backgroundColor: 'var(--color-gray-50)',
        },
      }),
      EditorView.lineWrapping,
    ]

    const handleChange = (val: string) => {
      if (formContext && name) {
        formContext.setValue(name, val)
      } else {
        setInternalValue(val)
        onChange?.(val)
      }
    }

    const hasError = formError || finalState === 'error'

    return (
      <div ref={ref} className={styles.container({ className })} {...props}>
        {label && (
          <label
            htmlFor={name}
            className="block text-sm font-normal leading-6 text-gray-900 mb-2"
          >
            {label}
          </label>
        )}

        <div
          className={`border rounded-md bg-white overflow-hidden ${
            hasError
              ? 'border-danger-500'
              : formContext && name
                ? 'border-gray-200 focus-within:border-brand-500 focus-within:shadow-[0_0_0_2px_var(--color-brand-100)]'
                : 'border-gray-200'
          } ${disabled ? 'bg-gray-50 border-gray-300 opacity-60' : ''}`}
        >
          <CodeMirror
            value={currentValue}
            onChange={handleChange}
            extensions={extensions}
            placeholder={placeholder}
            editable={!disabled}
            basicSetup={{
              lineNumbers: false,
              foldGutter: false,
            }}
          />
        </div>

        {finalHelperText && (
          <p className={styles.helper()}>{finalHelperText}</p>
        )}
      </div>
    )
  },
)
